import React, { useEffect, useRef, useState } from "react";
import SectionHeading from "./SectionHeading";
import axiosInstance from "../../../utils/axios";
import aafsa_in_numbers_bg from "../../../Assets/Images/home/aafsa_in_numbers/aafsa_in_numbers_bg.png";

const useCountUpOnView = (targetValue, duration = 2200) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            let start = 0;
            const end = Number(targetValue) || 0;
            const increment = end / (duration / 16);

            const timer = setInterval(() => {
              start += increment;
              if (start >= end) {
                setCount(end);
                clearInterval(timer);
              } else {
                setCount(Math.floor(start));
              }
            }, 16);
          }
        });
      },
      { threshold: 0.4 }
    );

    observer.observe(node);
    return () => node && observer.unobserve(node);
  }, [targetValue, duration, hasAnimated]);

  return { count, ref };
};

const StatCircle = ({ value, suffix, label, bg }) => {
  const { count, ref } = useCountUpOnView(value);

  return (
    <div
      ref={ref}
      className="flex flex-col items-center justify-center group w-full max-w-xs mx-auto p-2 sm:p-3 md:p-4"
    >
      <div
        className="
          relative flex flex-col items-center justify-center
          aspect-square w-full max-w-[8rem] sm:max-w-[10rem]
          md:max-w-[12rem] lg:max-w-[11rem] xl:max-w-[13rem]
          rounded-full border-2 border-white shadow-lg
          bg-transparent backdrop-blur-sm overflow-hidden
          transition-transform duration-500 ease-out
          hover:scale-110 group-hover:shadow-2xl
        "
      >
        {/* BG Overlay */}
        <div
          className="
            absolute inset-0 bg-cover bg-center bg-no-repeat
            opacity-0 group-hover:opacity-90
            transition-opacity duration-700 ease-out
          "
          style={{ backgroundImage: `url(${bg})` }}
        />

        <div
          className="
            absolute inset-0 bg-black/20
            opacity-0 group-hover:opacity-100
            transition-opacity duration-700 ease-out
          "
        />

        {/* Content */}
        <div className="relative z-10 flex flex-col items-center justify-center h-full w-full px-2 sm:px-3 md:px-4">
          <span
            className="
              font-bold text-white drop-shadow-lg 
              transition-transform duration-500 ease-out 
              group-hover:scale-110 text-center leading-none
            "
            style={{ fontSize: "clamp(1.25rem, 4vw, 2.5rem)" }}
          >
            {count}
            {suffix}
          </span>

          <p
            className="
              font-medium md:font-semibold text-center 
              text-[#FCC101] uppercase tracking-wide leading-tight 
              mt-1 sm:mt-2 px-1 break-words hyphens-auto
              transition-all duration-500 ease-out 
              group-hover:text-yellow-300
            "
            style={{ fontSize: "clamp(0.6rem, 1.8vw, 0.85rem)" }}
          >
            {label}
          </p>
        </div>
      </div>
    </div>
  );
};

const StatCircleSkeleton = () => (
  <div className="flex flex-col items-center justify-center w-full max-w-xs mx-auto p-4">
    <div className="relative flex flex-col items-center justify-center aspect-square w-full max-w-[10rem] rounded-full bg-gray-300/40 animate-pulse"></div>
    <div className="h-3 w-20 bg-gray-300/50 rounded mt-3 animate-pulse"></div>
  </div>
);

const AFSANumbers = () => {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchStats = async () => {
    try {
      const resp = await axiosInstance.get("/impact-stats");
      const data = resp?.data?.data ?? [];
      setStats(data);
    } catch (error) {
      console.error("Error loading stats:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  return (
    <div
      style={{
        backgroundImage: `url(${aafsa_in_numbers_bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
      className="w-full bg-[#1d242d] flex flex-col gap-y-6 items-center justify-center general-p-m"
    >
      <SectionHeading
        subtitle={"AAFSA in numbers"}
        title={"Interventions Highlights"}
        subtitleClassName={"text-[#FCC101]"}
        titleClassName={"text-[#FFFFFF]"}
      />

      <div className="w-full mx-auto grid grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6 py-3 sm:py-5 md:py-7">
        {loading
          ? Array.from({ length: 5 }).map((_, i) => (
              <StatCircleSkeleton key={i} />
            ))
          : stats
              ?.slice(0, 5)
              .map((stat) => (
                <StatCircle
                  key={stat._id}
                  value={stat.value}
                  suffix={stat.suffix}
                  label={stat.label}
                  bg={stat.bg}
                />
              ))}
      </div>
    </div>
  );
};

export default AFSANumbers;

// import React, { useEffect, useRef, useState } from "react";
// import SectionHeading from "./SectionHeading";
// import aafsa_in_numbers_bg from "../../../Assets/Images/home/aafsa_in_numbers/aafsa_in_numbers_bg.png";
// import campaigns_bg from "../../../Assets/Images/home/aafsa_in_numbers/campaigns_bg.png";
// import scholarships_bg from "../../../Assets/Images/home/aafsa_in_numbers/scholarships_bg.png";
// import animal_rescue_bg from "../../../Assets/Images/home/aafsa_in_numbers/animal_rescue_bg.png";
// import plantation_bg from "../../../Assets/Images/home/aafsa_in_numbers/plantation_bg.png";
// import health_checkup_bg from "../../../Assets/Images/home/aafsa_in_numbers/health_checkup_bg.png";

// /* ---------------- Custom Hook ---------------- */
// const useCountUpOnView = (targetValue, duration = 2000) => {
//   const [count, setCount] = useState(0);
//   const ref = useRef(null);
//   const [hasAnimated, setHasAnimated] = useState(false);

//   useEffect(() => {
//     const node = ref.current;
//     if (!node) return;

//     const observer = new IntersectionObserver(
//       (entries) => {
//         entries.forEach((entry) => {
//           // Trigger when element is in the middle viewport
//           if (entry.isIntersecting && !hasAnimated) {
//             setHasAnimated(true);
//             let start = 0;
//             const end = parseInt(targetValue.replace(/\D/g, ""), 10); // extract numbers
//             const increment = end / (duration / 16); // ~60fps
//             const timer = setInterval(() => {
//               start += increment;
//               if (start >= end) {
//                 setCount(end);
//                 clearInterval(timer);
//               } else {
//                 setCount(Math.floor(start));
//               }
//             }, 16);
//           }
//         });
//       },
//       { threshold: 0.5 }
//     );

//     observer.observe(node);
//     return () => {
//       if (node) observer.unobserve(node);
//     };
//   }, [targetValue, duration, hasAnimated]);

//   return { count, ref };
// };

// /* ---------------- Component ---------------- */
// const AFSANumbers = () => {
//   const stats = [
//     { value: "569+", label: "CAMPAIGNS", bg: campaigns_bg },
//     { value: "150000+", label: "SCHOLARSHIP", bg: scholarships_bg },
//     { value: "200+", label: "ANIMAL RESCUE", bg: animal_rescue_bg },
//     { value: "100000+", label: "PLANTATION", bg: plantation_bg },
//     { value: "50+", label: "HEALTH CAMPS", bg: health_checkup_bg },
//   ];

//   const StatCircle = ({ value, label, bg }) => {
//     const { count, ref } = useCountUpOnView(value);

//     // keep original suffix like '+'
//     const suffix = value.replace(/[0-9]/g, "");

//     return (
//       <div
//         ref={ref}
//         className="flex flex-col items-center justify-center group w-full max-w-xs mx-auto p-2 sm:p-3 md:p-4"
//       >
//         <div className="relative flex flex-col items-center justify-center aspect-square w-full max-w-[8rem] sm:max-w-[10rem] md:max-w-[12rem] lg:max-w-[11rem] xl:max-w-[13rem] rounded-full border-2 border-white shadow-lg bg-transparent backdrop-blur-sm overflow-hidden transition-all duration-300 hover:scale-105 group-hover:shadow-2xl">
//           {/* Background image overlay */}
//           <div
//             className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-0 group-hover:opacity-90 transition-opacity duration-500 ease-in-out"
//             style={{ backgroundImage: `url(${bg})` }}
//           />
//           <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 ease-in-out" />

//           {/* Content */}
//           <div className="relative z-10 flex flex-col items-center justify-center h-full w-full px-2 sm:px-3 md:px-4">
//             <span
//               className="font-bold text-white drop-shadow-lg transition-all duration-300 group-hover:scale-110 text-center leading-none"
//               style={{ fontSize: "clamp(1.25rem, 4vw, 2.5rem)" }}
//             >
//               {count}
//               {suffix}
//             </span>
//             <p
//               className="font-medium md:font-semibold text-center text-[#FCC101] uppercase tracking-wide leading-tight mt-1 sm:mt-2 px-1 transition-all duration-300 group-hover:text-yellow-300 break-words hyphens-auto"
//               style={{ fontSize: "clamp(0.6rem, 1.8vw, 0.85rem)" }}
//             >
//               {label}
//             </p>
//           </div>
//         </div>
//       </div>
//     );
//   };

//   return (
//     <div
//       style={{
//         backgroundImage: `url(${aafsa_in_numbers_bg})`,
//         backgroundSize: "cover",
//         backgroundPosition: "center",
//         backgroundRepeat: "no-repeat",
//       }}
//       className="w-full bg-[#1d242d] flex flex-col gap-y-6 items-center justify-center general-p-m"
//     >
//       <SectionHeading
//         subtitle={"AAFSA in numbers"}
//         title={"Interventions Highlights"}
//         subtitleClassName={"text-[#FCC101]"}
//         titleClassName={"text-[#FFFFFF]"}
//       />
//       <div className="w-full mx-auto grid grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6 py-3 sm:py-5 md:py-7">
//         {stats.map((stat, index) => (
//           <StatCircle
//             key={index}
//             value={stat.value}
//             label={stat.label}
//             bg={stat.bg}
//           />
//         ))}
//       </div>
//     </div>
//   );
// };

// export default AFSANumbers;
